/**
 * Publishes one motion intensity for the whole tree, derived from the route.
 *
 * Mounts once, inside BrowserRouter. The routes do not share a layout - most
 * go through SiteLayout, /auth through AuthLayout, the account routes have
 * their own shell - so resolving here means one mounting point instead of
 * three, and no layout file changes.
 *
 * prefers-reduced-motion collapses to "quiet" here rather than in each
 * primitive, so the rule exists once instead of being re-remembered per file.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { motionLevelFor } from "./zones";
import type { MotionLevel } from "./tokens";

/** Defaults to "quiet" so a primitive rendered outside the provider is inert
    rather than loud. */
const MotionLevelContext = createContext<MotionLevel>("quiet");

export function MotionZone({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();

  const level = useMemo<MotionLevel>(
    () => (reduced ? "quiet" : motionLevelFor(pathname)),
    [pathname, reduced],
  );

  return <MotionLevelContext.Provider value={level}>{children}</MotionLevelContext.Provider>;
}

export function useMotionLevel(): MotionLevel {
  return useContext(MotionLevelContext);
}
